import React, { useContext } from 'react'
import { ClosedLockIcon, DeleteIcon, EditIcon, HorizontalDotsIcon, OpenedLockIcon } from '@entur/icons'
import { GridContainer, GridItem } from '@entur/grid'
import { OverflowMenu, OverflowMenuItem } from '@entur/menu'
import { Heading3, StrongText, SubParagraph } from '@entur/typography'
import { SmallAlertBox, useToast } from '@entur/alert'
import { colors, space } from '@entur/tokens'
import { ExpandableText } from '@entur/expand'
import { FamilyMember, FetchState, TicketTypePreference } from '../../api-clients/api-types'
import { toPrintFormat } from '../../utils/date-utils'
import { trackEvent } from '../../utils/tracking-utils'
import {
  adjustTicketRightsWithDraft,
  createTemporaryTicketRight,
  findActiveTicketRights,
  getYearForTicketRight,
} from '../../utils/ticket-rights-utils'
import { ModalState } from '../../types'
import { ticketTypeAdultText, ticketTypeSeniorCitizenText } from '../../hooks/use-customer-preferences'
import { MenuBtn } from '../overflow-menu-icon-btn/overflow-menu-icon-btn'
import { TicketRightCard } from '../ticket-rights/cards/ticket-right-card'
import { useEmployee } from '../../contexts/employee-context'
import { useApi } from '../../api-clients/client-hooks'
import { patchFamilyMember, postTicketRights } from '../../api-clients/eba-clients'
import { useFamilyMembers } from '../../contexts/family-members-context'
import CopyButton from '../copy-button/copy-button'
import { AllTicketRights } from '../ticket-rights/all-ticket-rights-list'
import { useClientInfo } from '../../contexts/client-info-context'
import { ReadOnlyContext } from '../../contexts/read-only-context'

const year = new Date().getFullYear()
const nextYear = year + 1

const calculateActiveTicketRights = (familyMember: FamilyMember, employeeId: string) => {
  const activeTicketRights = findActiveTicketRights(familyMember.ticketRights)

  if (activeTicketRights?.length === 1) {
    const forYear = getYearForTicketRight(activeTicketRights[0]) === year ? nextYear : year
    if (forYear === year)
      activeTicketRights?.unshift(createTemporaryTicketRight(forYear, employeeId, familyMember.ticketHolderCode))
    else activeTicketRights?.push(createTemporaryTicketRight(forYear, employeeId, familyMember.ticketHolderCode))
  }
  if (
    activeTicketRights?.length === 0 &&
    familyMember.draftTicketRights &&
    familyMember.draftTicketRights?.length > 0
  ) {
    activeTicketRights?.push(createTemporaryTicketRight(year, employeeId, familyMember.ticketHolderCode))
    activeTicketRights?.push(createTemporaryTicketRight(nextYear, employeeId, familyMember.ticketHolderCode))
  }

  return adjustTicketRightsWithDraft(activeTicketRights, familyMember.draftTicketRights)
}

type FamilyMemberCardProps = {
  familyMember: FamilyMember
  changeEvent: (familyMember: FamilyMember, modalState: ModalState) => void
}
const FamilyMemberCard: React.FC<FamilyMemberCardProps> = ({ familyMember, changeEvent }) => {
  const { hasSensitiveAccess } = useClientInfo()
  const { readOnly } = useContext(ReadOnlyContext)
  const { addToast } = useToast()
  const { employee } = useEmployee()
  const { setTicketRights, setAbstainFamilyMember } = useFamilyMembers()
  const [ticketRightsResponse, generateTicketRights] = useApi(postTicketRights, {
    successCallback: (ticketRights) => {
      setTicketRights(ticketRights)
    },
  })
  const [patchResponse, doPatch] = useApi(patchFamilyMember, {
    successCallback: (data) => {
      addToast({
        content: 'Familiemedlem er oppdatert!',
      })
      generateTicketRights({
        id: employee.id,
        body: {},
      })
      setAbstainFamilyMember(data)
    },
  })
  const lastUpdate = familyMember.updatedAt || familyMember.createdAt
  const lastUpdateBy = familyMember.updatedBy || familyMember.createdBy

  const abstainFromTicket = () => {
    doPatch({
      id: familyMember?.id,
      body: { abstainFromTicket: !familyMember.abstainFromTicket },
    }).then()
  }

  const activeTicketRights = calculateActiveTicketRights(familyMember, employee.id)
  const isError = ticketRightsResponse.status === FetchState.ERROR || patchResponse.status === FetchState.ERROR
  const familyMemberFullName = `${familyMember.firstName} ${familyMember.surname}`

  return (
    <div className="family-member-info" data-cy="family-member-info" style={{ marginBottom: space.extraLarge2 }}>
      <Heading3>
        {familyMemberFullName} ({familyMember.relation.description})
        <CopyButton value={familyMemberFullName} description="Navn" size="14" />
        {!readOnly && hasSensitiveAccess === true && (
          <div style={{ display: 'inline-block', marginLeft: space.medium }}>
            <OverflowMenu
              className="family-member-edit-menu"
              button={
                <MenuBtn
                  id={`family-member-edit-menu-${familyMember.id}`}
                  data-cy="family-member-edit-menu"
                  name="family-member-edit-menu"
                  style={{ display: 'inline-flex' }}
                >
                  <HorizontalDotsIcon />
                </MenuBtn>
              }
            >
              <OverflowMenuItem
                className={`family-member-edit-btn-${familyMember.id}`}
                data-cy="family-member-edit-button"
                onSelect={() => {
                  trackEvent('Open-Edit-Family-Member', { category: 'Family-Member' })
                  changeEvent(familyMember, ModalState.EDIT)
                }}
              >
                <EditIcon /> Endre familiemedlem
              </OverflowMenuItem>
              <OverflowMenuItem
                className={`family-member-abstain-${familyMember.id}`}
                data-cy="family-member-abstain-button"
                onSelect={abstainFromTicket}
              >
                {!familyMember.abstainFromTicket ? <ClosedLockIcon /> : <OpenedLockIcon />}
                {!familyMember.abstainFromTicket ? 'Sperr rettighet' : 'Åpne rettighet'}
              </OverflowMenuItem>
              <OverflowMenuItem
                className={`family-member-delete-btn-${familyMember.id}`}
                data-cy="family-member-delete-button"
                onSelect={() => {
                  trackEvent('Delete-Family-Member', { category: 'Family-Member' })
                  changeEvent(familyMember, ModalState.DELETE)
                }}
              >
                <DeleteIcon />
                Slett familiemedlem
              </OverflowMenuItem>
            </OverflowMenu>
          </div>
        )}
      </Heading3>
      <SubParagraph style={{ marginBottom: space.extraSmall, marginTop: 0 }}>
        <StrongText> Kode: </StrongText>
        {familyMember.ticketHolderCode} <CopyButton value={familyMember.ticketHolderCode} description="Kode" />
        &emsp;
        <StrongText> Fødselsnummer: </StrongText>
        {toPrintFormat(familyMember.dateOfBirth)}
        {familyMember.ssn && <> {familyMember.ssn}</>}
        <CopyButton value={toPrintFormat(familyMember.dateOfBirth)} description="Fødselsdato" />
        &emsp;
        <StrongText> Sperret rettighet: </StrongText>
        {familyMember.abstainFromTicket ? 'Sperret' : 'Ikke sperret'}&emsp;
        {familyMember.relation.code !== 'Barn' && (
          <>
            <StrongText> Billettype: </StrongText>
            {familyMember.customerPreferences?.ticketTypePreference == TicketTypePreference.SENIOR_CITIZEN
              ? ticketTypeSeniorCitizenText
              : ticketTypeAdultText}
          </>
        )}
        {lastUpdate && (
          <span style={{ float: 'right', color: colors.blues.blue40 }}>
            Sist oppdatert av {lastUpdateBy} {toPrintFormat(lastUpdate)}
          </span>
        )}
      </SubParagraph>
      <GridContainer spacing="large">
        {isError && (
          <>
            <GridItem small={6}>
              <SmallAlertBox variant="error"> Rettighet ble ikke generert på nytt.</SmallAlertBox>
            </GridItem>
            <GridItem small={6} />
          </>
        )}
        {activeTicketRights.length > 0 &&
          activeTicketRights?.map((ticketRightWithDraft) => (
            <GridItem small={12} medium={6} key={ticketRightWithDraft.id}>
              <TicketRightCard ticketRightWithDraft={ticketRightWithDraft} familyMember={familyMember} />
            </GridItem>
          ))}
        {activeTicketRights.length === 0 && (
          <GridItem
            style={{
              backgroundColor: colors.greys.grey90,
              padding: space.small,
              color: colors.blues.blue40,
              fontStyle: 'italic',
            }}
            small={5}
          >
            Ingen nåværende rettigheter
          </GridItem>
        )}
      </GridContainer>
      <ExpandableText title="Tidligere rettigheter" style={{ marginTop: space.extraSmall2 }}>
        <AllTicketRights ticketRights={familyMember.ticketRights} />
      </ExpandableText>
    </div>
  )
}

export default FamilyMemberCard
